export type PoeCfgRow = {
  name?: string;
  enable?: boolean;
  prio?: number;
  limit?: number;
  limit_pwr?: number;
  fault?: boolean;
};

export type PoeStateRow = {
  poeclass?: number;
  status?: number;
  volt?: number;
  curr?: number;
};

function pickLimitW(entry: unknown): string | null {
  if (entry == null) return null;
  if (typeof entry === 'number' && !Number.isNaN(entry)) return `${entry}W`;
  if (typeof entry === 'string' && entry.trim() !== '')
    return `${entry.trim()}W`;
  if (typeof entry === 'object' && 'limit' in (entry as object)) {
    const l = (entry as { limit?: unknown }).limit;
    if (typeof l === 'number' && !Number.isNaN(l)) return `${l}W`;
    if (typeof l === 'string' && l.trim() !== '') return `${l.trim()}W`;
  }
  return null;
}

/** Normalizes `get limit` responses: number, { limit }, or array of those (multi-supply). */
export function formatPoeLimitResponse(data: unknown): string {
  if (data == null) return '-';
  const single = pickLimitW(data);
  if (single) return single;
  if (Array.isArray(data)) {
    if (data.length === 0) return '-';
    const parts = data.map((x) => pickLimitW(x)).filter(Boolean) as string[];
    return parts.length ? parts.join(' / ') : '-';
  }
  return '-';
}

function formatUsedSlice(x: { usedBudget?: unknown }): string {
  const u = Number(x.usedBudget);
  if (Number.isNaN(u)) return '-';
  if (u < 0) return 'PoE Failure';
  return `${u / 10}W`;
}

/** Normalizes `get global state`: single object or array (e.g. redundant PSUs). */
export function formatPoeGlobalBudget(global: unknown): string {
  if (global == null) return '-';
  if (Array.isArray(global)) {
    if (global.length === 0) return '-';
    return global
      .map((x) => formatUsedSlice(x as { usedBudget?: unknown }))
      .join(' / ');
  }
  return formatUsedSlice(global as { usedBudget?: unknown });
}

/** Ensures port-ordered arrays; some firmware may return { "0": {...}, "1": {...} }. */
export function normalizePoePortArray<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    const keys = Object.keys(o).filter((k) => /^\d+$/.test(k));
    if (keys.length) {
      return keys.sort((a, b) => Number(a) - Number(b)).map((k) => o[k] as T);
    }
  }
  return [];
}

export function priorityLabel(prio?: number): string {
  if (prio === 1) return 'critical';
  if (prio === 2) return 'high';
  return 'low';
}

const POE_STATUS_LABELS = [
  'disabled',
  'not detected',
  'powered',
  'blocked',
  'dropped',
  'error',
] as const;

export function poeStatusLabel(status?: number): string {
  const idx = status ?? 0;
  return idx >= 0 && idx < POE_STATUS_LABELS.length
    ? POE_STATUS_LABELS[idx]
    : '-';
}
